import { useDispatch, useSelector } from "react-redux";
import { useDebounceCallback } from "usehooks-ts";
import { useEffect, useState } from "react";

import { StorytellingOptionsMenu } from "../../../../../StorytellingOptionsMenu";
import {
  TextAndImageHeadingContent,
  TextAndImageHeading,
  TextAndImageHighlights,
  TextAndImageDescription,
} from "../../../../styles";
import { SectionWrapper } from "../../../../styles";
import { getIsEditMode, getIsPublicMode } from "../../../../../../store/selectors/main";
import { getPageBanner, getPageSettings } from "../../../../../../store/selectors/projects";
import { requestUpdatePage } from "../../../../../../store/slices/projectPages";
import {
  BlockLoader,
  BorderedSettingsBlock,
  SettingsWrapper,
} from "../../styles";
import { CenterIcon, LeftIcon, RightIcon } from "../../icons/TextPositionIcons";
import {
  requestDownloadFile,
  requestUploadSimpleFile,
} from "../../../../../../store/slices/files";
import { TextLoader } from "../../../../../LoaderText";
import { IPageHeader } from "../../../../../../store/slices/pageContentGenerator";

interface Props {
  currentPageId: string;
  contentItem?: IPageHeader;
  setContentItem: (item: IPageHeader) => void;
}

const TextImage: React.FC<Props> = ({
  currentPageId,
  contentItem,
  setContentItem,
}) => {
  const currentPage = useSelector(getPageSettings);
  const isEditMode = useSelector(getIsEditMode);
  const isPublic = useSelector(getIsPublicMode);
  const header = useSelector(getPageBanner);

  const [open, setOpen] = useState(false);

  const [file, setFile] = useState();

  const dispatch = useDispatch();
  const debouncedFile = useDebounceCallback(setFile, 1000);

  useEffect(() => {
    const contentItemJson = JSON.stringify(contentItem);
    const headerJson = JSON.stringify(header);
    if (headerJson !== contentItemJson) {
      dispatch(
        requestUpdatePage({
          ...currentPage,
          header: {
            ...contentItem,
            image: contentItem?.backgroundColor
              ? undefined
              : contentItem?.image,
          },
        })
      );
      if (contentItem?.backgroundColor) {
        setFile(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentItem]);

  useEffect(() => {
    if (file && typeof file !== "string") {
      try {
        dispatch(
          requestUploadSimpleFile({
            files: [file],
            callbacks: {
              onSuccess: (resp) => {
                const uploadedFile = resp?.at(0);
                if (uploadedFile) {
                  dispatch(
                    requestUpdatePage({
                      ...currentPage,
                      header: {
                        ...header,
                        image: uploadedFile.path,
                        backgroundColor: undefined,
                      },
                    })
                  );
                }
              },
            },
          })
        );
      } catch (e) { }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (header?.image) {
      dispatch(
        requestDownloadFile({
          path: header?.image,
          callbacks: {
            onSuccess: (resp) => {
              setFile(resp.url);
            },
          },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header]);

  return (
    <div id={currentPageId}>
      <SectionWrapper
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        $backgroundColor={contentItem?.backgroundColor}
        $imageUrl={typeof file === "string" ? file : undefined}
        $gradient={contentItem?.gradient}
        $rgbaColor={contentItem?.rgbaColor}
        $isEditMode={!isPublic && isEditMode}
        spellCheck={false}
      >
        {!isPublic && isEditMode && (
          <>
            <StorytellingOptionsMenu
              item={contentItem}
              setContentItem={setContentItem}
              setFile={debouncedFile}
              fileUrl={file}
            />
            <SettingsWrapper $hover={open}>
              <BorderedSettingsBlock>
                <LeftIcon
                  type={contentItem?.align}
                  onClick={() => {
                    setContentItem({ ...contentItem, align: "left" });
                  }}
                />
                <CenterIcon
                  type={contentItem?.align}
                  onClick={() => {
                    setContentItem({ ...contentItem, align: "center" });
                  }}
                />
                <RightIcon
                  type={contentItem?.align}
                  onClick={() => {
                    setContentItem({ ...contentItem, align: "right" });
                  }}
                />
              </BorderedSettingsBlock>
            </SettingsWrapper>
          </>
        )}

        <TextAndImageHeadingContent $alignment={contentItem?.align}>
          {contentItem?.title ? (
            <TextAndImageHeading
              defaultValue={contentItem?.title}
              $color={contentItem?.textColor!}
              spellCheck={false}
              contentEditable={!isPublic && isEditMode}
              $alignment={contentItem?.align}
              $isEditMode={!isPublic && isEditMode}
              onChange={(e) => {
                setContentItem({ ...contentItem, title: e.target.value });
              }}
              maxLength={59}
            />
          ) : (
            <div style={{ marginTop: "80px", width: "400px" }}>
              <BlockLoader>
                <TextLoader opacity={0.6} />
                <TextLoader opacity={0.6} />
                <TextLoader opacity={0.6} />
              </BlockLoader>
            </div>
          )}
          {contentItem?.description ? (
            <TextAndImageHighlights
              defaultValue={contentItem?.description}
              $color={contentItem?.textColor!}
              spellCheck={false}
              contentEditable={!isPublic && isEditMode}
              $alignment={contentItem?.align}
              $isEditMode={!isPublic && isEditMode}
              maxLength={240}
              onChange={(e) =>
                setContentItem({ ...contentItem, description: e.target.value })
              }
            />
          ) : (
            <div style={{ marginTop: "24px" }}>
              <BlockLoader>
                <TextLoader opacity={0.6} />
                <TextLoader opacity={0.6} />
                <TextLoader opacity={0.6} />
              </BlockLoader>
            </div>
          )}
          <br />
          {contentItem?.title && contentItem?.description ? (
            <TextAndImageDescription
              defaultValue={contentItem?.insights}
              $color={contentItem?.textColor!}
              spellCheck={false}
              contentEditable={!isPublic && isEditMode}
              $alignment={contentItem?.align}
              $isEditMode={!isPublic && isEditMode}
              maxLength={240}
              onChange={(e) =>
                setContentItem({ ...contentItem, insights: e.target.value })
              }
            />
          ) : (
            <div style={{ marginTop: "24px" }}>
              <BlockLoader>
                <TextLoader opacity={0.6} />
                <TextLoader opacity={0.6} />
                <TextLoader opacity={0.6} />
              </BlockLoader>
            </div>
          )}
        </TextAndImageHeadingContent>
      </SectionWrapper>
    </div>
  );
};

export default TextImage;
